import { InjectionToken, inject } from '@angular/core';
import { provideFirebaseApp, initializeApp, FirebaseApp } from '@angular/fire/app';
import { provideAuth, getAuth, Auth, connectAuthEmulator } from '@angular/fire/auth';
import { provideFirestore, getFirestore, Firestore, connectFirestoreEmulator } from '@angular/fire/firestore';
import { connectFunctionsEmulator, Functions, getFunctions, provideFunctions } from '@angular/fire/functions';
import { provideStorage, getStorage, Storage, connectStorageEmulator } from '@angular/fire/storage';

// Define Injection Tokens for Firebase services
export const FIREBASE_APP = new InjectionToken<FirebaseApp>('FIREBASE_APP');
export const FIRESTORE = new InjectionToken<Firestore>('FIRESTORE');
export const FIREBASE_AUTH = new InjectionToken<Auth>('FIREBASE_AUTH');
export const FIREBASE_STORAGE = new InjectionToken<Storage>('FIREBASE_STORAGE');
export const FIREBASE_FUNCTIONS = new InjectionToken<any>('FIREBASE_FUNCTIONS');

// ✅ Now `provideFireAuthLib()` ensures Firebase is initialized before using it
export function provideFireAuthLib(firebaseConfig: object, useEmulators: boolean) {
  return [
    provideFirebaseApp(() => initializeApp(firebaseConfig)), // ✅ Initialize Firebase
    { provide: FIREBASE_APP, useExisting: FirebaseApp },

    provideAuth(() => {
      const app = inject(FIREBASE_APP); // ✅ Ensure FirebaseApp is injected before using
      const auth = getAuth(app);
      if (useEmulators) {
        connectAuthEmulator(auth, 'http://localhost:7003', { disableWarnings: false });
      }
      return auth;
    }),
    { provide: FIREBASE_AUTH, useFactory: () => inject(Auth) },

    provideFirestore(() => {
      const app = inject(FIREBASE_APP); // ✅ Ensure FirebaseApp is injected before using
      const firestore = getFirestore(app);
      if (useEmulators) {
        connectFirestoreEmulator(firestore, 'localhost', 7005);
      }
      return firestore;
    }),
    { provide: FIRESTORE, useFactory: () => inject(Firestore) },

    provideStorage(() => {
      const app = inject(FIREBASE_APP); // ✅ Ensure FirebaseApp is injected before using
      const storage = getStorage(app);
      if (useEmulators) {
        connectStorageEmulator(storage, 'localhost', 7007);
      }
      return storage;
    }),
    { provide: FIREBASE_STORAGE, useFactory: () => inject(Storage) },

    provideFunctions(() => {
      const app = inject(FIREBASE_APP); // ✅ Ensure FirebaseApp is injected before using
      const functions = getFunctions(app);
      if (useEmulators) {
        connectFunctionsEmulator(functions, 'localhost', 7004);
      }
      return functions;
    }),
    { provide: FIREBASE_FUNCTIONS, useFactory: () => inject(Functions) }
  ];
}